import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Loader2, Save, Wallet, RefreshCw } from "lucide-react";

const usd = (v) => `US$ ${Number(v || 0).toFixed(2)}`;

function BudgetRow({ b, value, onChange }) {
  const reached = b.state === "limit_reached";
  return (
    <div className="border border-softblue p-3 space-y-2" data-testid={`ai-budget-row-${b.bucket}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-blueblack">{b.label}</span>
        <span data-testid={`ai-budget-state-${b.bucket}`}
              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${reached ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
          {reached ? "Límite alcanzado" : "Disponible"}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-textgray">Presupuesto diario (US$)</Label>
          <Input type="number" step="0.05" min="0" max="100" value={value}
                 onChange={(e) => onChange(e.target.value)} data-testid={`ai-budget-input-${b.bucket}`} />
        </div>
        <div><div className="text-textgray">Consumo estimado hoy</div><div className="font-mono" data-testid={`ai-budget-spent-${b.bucket}`}>{usd(b.spent_est_usd)}</div></div>
        <div><div className="text-textgray">Restante</div><div className="font-mono" data-testid={`ai-budget-remaining-${b.bucket}`}>{usd(b.remaining_usd)}</div></div>
        <div><div className="text-textgray">Utilizado</div><div className="font-mono" data-testid={`ai-budget-pct-${b.bucket}`}>{Math.min(100, b.used_pct)}%</div></div>
      </div>
      <Progress value={Math.min(100, b.used_pct)} className="h-1.5" />
      <div className="text-[11px] text-textgray">
        {b.calls} llamadas · {b.refused} rechazadas por presupuesto
        {b.reserved_in_flight_usd > 0 ? ` · ${usd(b.reserved_in_flight_usd)} reservado en curso` : ""}
      </div>
    </div>
  );
}

export function AiBudgetSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.adminAiBudgetGet();
      setData(d);
      setForm(Object.fromEntries(d.buckets.map((b) => [b.bucket, b.limit_usd])));
    } catch { toast.error("No se pudo cargar el presupuesto de IA"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v)]));
      const d = await api.adminAiBudgetPut(body);
      setData(d);
      toast.success("Presupuesto de IA guardado");
    } catch { toast.error("No se pudo guardar el presupuesto"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="text-textgray text-sm"><Loader2 className="inline w-4 h-4 animate-spin mr-2" />Cargando presupuesto…</div>;

  return (
    <div className="bg-white border border-softblue p-4 space-y-3" data-testid="ai-budget-section">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-base font-bold text-blueblack flex items-center gap-2">
          <Wallet className="w-4 h-4" /> Presupuesto diario de IA
        </h3>
        <Button variant="ghost" size="sm" onClick={load} data-testid="ai-budget-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <p className="text-xs text-textgray" data-testid="ai-budget-note">
        {data.note} Se reinicia el <b data-testid="ai-budget-reset">{data.reset_at_local}</b> (hora Ushuaia).
        Al agotarse: la relevancia sigue con heurística, la redacción se detiene sin perder candidatas y el asistente muestra un aviso. La publicación automática continúa.
      </p>
      {data.buckets.map((b) => (
        <BudgetRow key={b.bucket} b={b} value={form[b.bucket] ?? ""} onChange={(v) => setForm((f) => ({ ...f, [b.bucket]: v }))} />
      ))}
      <Button onClick={save} disabled={saving} className="gap-2" data-testid="ai-budget-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar presupuesto
      </Button>
    </div>
  );
}
